import { isFacility } from './smartChoice.utils';

/**
 * Field visibility configuration for Facility Smart Choice audit modal
 */
export interface AuditModalFieldVisibility {
  quality: {
    credentialed: boolean;
    uhpd: boolean;
    llmScore: boolean;
    leapfrog: boolean;
    accreditation: boolean;
  };
  benefits: {
    showAll: boolean;
  };
  convenience: {
    showAll: boolean;
    appointmentAvailability: boolean;
    eveningAppointments: boolean;
    weekendAppointments: boolean;
    acceptingNewPatients: boolean;
    onlineAppointmentScheduling: boolean;
    distance: boolean;
    virtualAppointments: boolean;
    memberPreferredRadius: boolean;
  };
  personalFit: {
    language: boolean;
    preferredLanguages: boolean;
    gender: boolean;
    boardCertification: boolean;
    consumerRating: boolean;
    consumerRatingSurveyVolume: boolean;
    midPointRatingIndicator: boolean;
  };
  verification: {
    showSection: boolean;
  };
  randomization: {
    showSection: boolean;
  };
}

/**
 * Returns field visibility configuration for audit modal based on provider context
 * This function is the single source of truth for what fields show in the audit modal
 *
 * @param chipValue - Current chip category (e.g., 'Facilities', 'Dental')
 * @param providerType - Provider type (e.g., 'Organization', 'Practitioner')
 * @returns Field visibility configuration object
 *
 * @example
 * const visibility = getAuditModalFieldVisibility('Facilities', 'Organization');
 * if (visibility.quality.leapfrog) {
 *   // Show leapfrog field
 * }
 */
export function getAuditModalFieldVisibility(
  chipValue: string,
  providerType: string
): AuditModalFieldVisibility {
  // Facility-specific configuration
  if (isFacility(chipValue, providerType)) {
    return {
      quality: {
        credentialed: true,
        uhpd: false,
        llmScore: false,
        leapfrog: false, // Baseline = N, don't show
        accreditation: false, // Baseline = N, don't show
      },
      benefits: {
        showAll: true,
      },
      convenience: {
        showAll: true,
        appointmentAvailability: false, // Not in baseline
        eveningAppointments: true, // Baseline Y
        weekendAppointments: true, // Baseline Y
        acceptingNewPatients: true, // Baseline Y
        onlineAppointmentScheduling: false, // Not in baseline
        distance: true, // Baseline Y
        virtualAppointments: true, // Baseline Y
        memberPreferredRadius: false, // Not in baseline
      },
      personalFit: {
        language: true, // Baseline Y - Specified Language Spoken
        preferredLanguages: true,
        gender: false,
        boardCertification: false,
        consumerRating: false, // Not in baseline table for facilities
        consumerRatingSurveyVolume: false, // Not in baseline table for facilities
        midPointRatingIndicator: false,
      },
      verification: {
        showSection: true, // Address/Phone confidence both N for facilities
      },
      randomization: {
        showSection: true,
      },
    };
  }

  // Future: Add dental configuration
  // if (isDental(chipValue, providerType)) { return {...}; }

  // Future: Add behavioral health configuration
  // if (isBehavioralHealth(chipValue, providerType)) { return {...}; }

  // Default: Practitioner configuration
  return {
    quality: {
      credentialed: true,
      uhpd: true,
      llmScore: true,
      leapfrog: false,
      accreditation: false,
    },
    benefits: {
      showAll: true,
    },
    convenience: {
      showAll: true,
      appointmentAvailability: true,
      eveningAppointments: true,
      weekendAppointments: true,
      acceptingNewPatients: true,
      onlineAppointmentScheduling: true,
      distance: true,
      virtualAppointments: true,
      memberPreferredRadius: true,
    },
    personalFit: {
      language: true,
      preferredLanguages: true,
      gender: true,
      boardCertification: true,
      consumerRating: true,
      consumerRatingSurveyVolume: true,
      midPointRatingIndicator: true,
    },
    verification: {
      showSection: true,
    },
    randomization: {
      showSection: true,
    },
  };
}

/**
 * NEW HYBRID APPROACH: Returns only fields that should be visible as arrays
 * This eliminates false condition checks and improves performance
 *
 * @param chipValue - Current chip category (e.g., 'Facilities', 'Dental')
 * @param providerType - Provider type (e.g., 'Organization', 'Practitioner')
 * @returns Object containing arrays of visible fields and custom labels
 *
 * @example
 * const config = getVisibleFieldsConfig('Facilities', 'Organization');
 * if (config.quality.fields.includes('credentialed')) {
 *   // Show field - no false checks for fields that shouldn't show!
 * }
 */
export interface VisibleFieldsConfig {
  quality: {
    fields: string[];
    labels?: Record<string, string>;
  };
  benefits: {
    fields: string[];
    labels?: Record<string, string>;
    showPractitionerFields: boolean; // For the two different benefit UIs
  };
  convenience: {
    fields: string[];
    labels?: Record<string, string>;
  };
  personalFit: {
    fields: string[];
    labels?: Record<string, string>;
  };
  verification: {
    showSection: boolean;
  };
  randomization: {
    showSection: boolean;
  };
}

export function getVisibleFieldsConfig(
  chipValue: string,
  providerType: string
): VisibleFieldsConfig {
  // Facility-specific configuration
  if (isFacility(chipValue, providerType)) {
    return {
      quality: {
        fields: ['credentialed'],
        labels: {
          credentialed: 'Facility Credentialing Status',
        },
      },
      benefits: {
        fields: [
          'inNetworkStatus',
          'providerTieringLevel',
          'costEfficiencyRating',
          'preferredLabNetworkIndicator',
          'oxfordPreferredLabNetworkIndicator',
          'designatedDiagnosticLab',
          'freeStandingFacilityIndicator',
        ],
        labels: {},
        showPractitionerFields: false, // Show facility benefit UI
      },
      convenience: {
        fields: [
          'eveningAppointments',
          'weekendAppointments',
          'acceptingNewPatients',
          'distance',
          'virtualAppointments',
        ],
        labels: {},
      },
      personalFit: {
        fields: ['language', 'preferredLanguages'],
        labels: {
          language: 'SMART_CHOICE.AUDIT_SCREEN.Language Spoken - Facility',
        },
      },
      verification: {
        showSection: true,
      },
      randomization: {
        showSection: true,
      },
    };
  }

  // Future: Add dental configuration
  // if (isDental(chipValue, providerType)) { return {...}; }

  // Default: Practitioner configuration
  return {
    quality: {
      fields: ['credentialed', 'uhpd', 'llmScore'],
      labels: {
        credentialed: 'Provider Credentialing',
      },
    },
    benefits: {
      fields: [
        'inNetworkStatus',
        'costEfficiencyRating',
        'providerTierDesignation',
        'acoParticipationStatus',
        'preferredProviderStatus',
        'costIndicator',
        'billingCode',
        'bandedCost',
      ],
      labels: {},
      showPractitionerFields: true, // Show practitioner benefit UI
    },
    convenience: {
      fields: [
        'appointmentAvailability',
        'eveningAppointments',
        'weekendAppointments',
        'acceptingNewPatients',
        'onlineAppointmentScheduling',
        'distance',
        'virtualAppointments',
        'memberPreferredRadius',
      ],
      labels: {},
    },
    personalFit: {
      fields: [
        'language',
        'preferredLanguages',
        'gender',
        'boardCertification',
        'consumerRating',
        'consumerRatingSurveyVolume',
        'midPointRatingIndicator',
      ],
      labels: {
        language: 'Language Spoken - Practitioner',
      },
    },
    verification: {
      showSection: true,
    },
    randomization: {
      showSection: true,
    },
  };
}
